import { addDays, roundToNearestMinutes, set } from 'date-fns'

export interface TimeSpan {
  start: Date
  end: Date
}

export type PauseError = 'outside' | 'order' | 'overlap'

const DEFAULT_PAUSE_SECONDS = 30 * 60
const STEP_MINUTES = 5
const STEP_MS = STEP_MINUTES * 60 * 1000

/**
 * The first moment at or after `anchor` whose clock time matches `time`.
 * Pause times are picked with a time-only input, so this resolves which day
 * they fall on when a registration crosses midnight.
 */
export function placeTimeAfter(time: Date, anchor: Date): Date {
  const placed = set(anchor, {
    hours: time.getHours(),
    minutes: time.getMinutes(),
    seconds: 0,
    milliseconds: 0,
  })

  return placed < anchor ? addDays(placed, 1) : placed
}

export function anchorPause(pause: TimeSpan, start: Date): TimeSpan {
  const pauseStart = placeTimeAfter(pause.start, start)
  return { start: pauseStart, end: placeTimeAfter(pause.end, pauseStart) }
}

export function sortSpans<T extends TimeSpan>(spans: T[]): T[] {
  return [...spans].sort((a, b) => a.start.getTime() - b.start.getTime())
}

export function validatePause(
  span: TimeSpan,
  pause: TimeSpan
): PauseError | null {
  if (pause.start >= pause.end) return 'order'
  if (pause.start <= span.start || pause.end >= span.end) return 'outside'
  return null
}

/** Touching pauses count as overlapping, since they would leave an empty entry between them. */
export function validatePauses(
  span: TimeSpan,
  pauses: TimeSpan[]
): (PauseError | null)[] {
  return pauses.map((pause, idx) => {
    const error = validatePause(span, pause)
    if (error) return error

    const overlaps = pauses.some(
      (other, otherIdx) =>
        otherIdx !== idx && other.start <= pause.end && other.end >= pause.start
    )
    return overlaps ? 'overlap' : null
  })
}

export function splitAroundPauses(
  span: TimeSpan,
  pauses: TimeSpan[]
): TimeSpan[] {
  const entries: TimeSpan[] = []
  let cursor = span.start

  for (const pause of sortSpans(pauses)) {
    entries.push({ start: cursor, end: pause.start })
    cursor = pause.end
  }
  entries.push({ start: cursor, end: span.end })

  return entries
}

/**
 * A new pause of the configured length, centred in the longest stretch of
 * work left. Shortened to fit, or `null` when no stretch has room for one.
 */
export function getNextPause(
  span: TimeSpan,
  pauses: TimeSpan[],
  pauseDurationSeconds: number
): TimeSpan | null {
  const longest = splitAroundPauses(span, pauses).reduce<TimeSpan | null>(
    (best, entry) =>
      !best || getSpanSeconds([entry]) > getSpanSeconds([best]) ? entry : best,
    null
  )
  if (!longest) return null

  const room = longest.end.getTime() - longest.start.getTime() - 2 * STEP_MS
  if (room < STEP_MS) return null

  const duration = Math.min(
    (pauseDurationSeconds || DEFAULT_PAUSE_SECONDS) * 1000,
    room
  )
  const middle = (longest.start.getTime() + longest.end.getTime()) / 2
  const earliest = longest.start.getTime() + STEP_MS
  const latest = longest.end.getTime() - STEP_MS - duration
  const start = Math.min(
    Math.max(
      roundToNearestMinutes(new Date(middle - duration / 2), {
        nearestTo: STEP_MINUTES,
      }).getTime(),
      earliest
    ),
    latest
  )

  return { start: new Date(start), end: new Date(start + duration) }
}

/** The free stretch a pause can be dragged within: up to its neighbours or the registration's ends. */
export function getPauseBounds(
  span: TimeSpan,
  pauses: TimeSpan[],
  index: number
): TimeSpan {
  const pause = pauses[index]
  if (!pause) return span

  const others = pauses.filter((_, idx) => idx !== index)
  const previous = others
    .filter(other => other.end <= pause.start)
    .reduce<Date>(
      (latest, other) => (other.end > latest ? other.end : latest),
      span.start
    )
  const next = others
    .filter(other => other.start >= pause.end)
    .reduce<Date>(
      (earliest, other) => (other.start < earliest ? other.start : earliest),
      span.end
    )

  return { start: previous, end: next }
}

export function getSpanSeconds(spans: TimeSpan[]) {
  return spans.reduce(
    (acc, span) => acc + (span.end.getTime() - span.start.getTime()) / 1000,
    0
  )
}

export function formatSecondsAsDuration(seconds: number) {
  const totalMinutes = Math.max(0, Math.round(seconds / 60))
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60

  return `${hours}:${minutes.toString().padStart(2, '0')}`
}

export type PauseDragMode = 'move' | 'start' | 'end'

/**
 * The pause after dragging it `deltaMs` in the preview. Snaps to five
 * minutes and keeps at least one step of work between it and `bounds` (the
 * registration's ends or a neighbouring pause) and one step of pause, so the
 * split never produces an empty entry.
 */
export function dragPause(
  pause: TimeSpan,
  bounds: TimeSpan,
  mode: PauseDragMode,
  deltaMs: number
): TimeSpan {
  const step = STEP_MS
  const min = bounds.start.getTime() + step
  const max = bounds.end.getTime() - step
  const snap = (ms: number) =>
    roundToNearestMinutes(new Date(ms), {
      nearestTo: STEP_MINUTES,
    }).getTime()
  const clamp = (ms: number, low: number, high: number) =>
    Math.min(Math.max(ms, low), high)

  const start = pause.start.getTime()
  const end = pause.end.getTime()

  if (mode === 'move') {
    const duration = end - start
    if (max - min < duration) return pause

    const newStart = clamp(snap(start + deltaMs), min, max - duration)
    return { start: new Date(newStart), end: new Date(newStart + duration) }
  }

  if (mode === 'start') {
    const newStart = clamp(snap(start + deltaMs), min, end - step)
    return { start: new Date(newStart), end: pause.end }
  }

  const newEnd = clamp(snap(end + deltaMs), start + step, max)
  return { start: pause.start, end: new Date(newEnd) }
}

export interface ActivitySpan extends TimeSpan {
  activityType: string
}

/**
 * Mirrors the backend's `update_paid_pause`: a rest no longer than the salary
 * work time's paid pause threshold is paid.
 */
export function isPaidPause(
  pause: TimeSpan,
  paidPauseThresholdSeconds: number | null | undefined
) {
  if (!paidPauseThresholdSeconds) return false
  return getSpanSeconds([pause]) <= paidPauseThresholdSeconds
}

/** Activities overlapping the span, cut to it, with short rests marked paid. */
export function clipActivitiesToSpan(
  activities: ActivitySpan[],
  span: TimeSpan,
  paidPauseThresholdSeconds: number | null | undefined
): ActivitySpan[] {
  return sortSpans(
    activities.filter(
      activity => activity.start < span.end && activity.end > span.start
    )
  ).map(activity => ({
    activityType:
      activity.activityType === 'Rest' &&
      isPaidPause(activity, paidPauseThresholdSeconds)
        ? 'Paid Rest'
        : activity.activityType,
    start: activity.start < span.start ? span.start : activity.start,
    end: activity.end > span.end ? span.end : activity.end,
  }))
}
